import React, { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsNotifications() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [newLead, setNewLead] = useState("");
    const [newConv, setNewConv] = useState("");

    useEffect(() => {
        adminApi.get("/api/admin/settings/notifications").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const set = (k, v) => setData({ ...data, [k]: v });

    const addEmail = (listKey, val, setter) => {
        const v = (val || "").trim().toLowerCase();
        if (!v || !v.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        const next = Array.from(new Set([...(data[listKey] || []), v]));
        set(listKey, next);
        setter("");
    };

    const removeEmail = (listKey, idx) => {
        set(listKey, data[listKey].filter((_, i) => i !== idx));
    };

    const save = async () => {
        setSaving(true);
        try {
            await adminApi.put("/api/admin/settings/notifications", data);
            toast.success("Notification preferences saved");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader
                eyebrow="Settings"
                title="Notifications"
                description="Customize how and when you receive notifications about new leads and conversations from Priya."
                testid="page-settings-notifications"
            />

            <NotifBlock
                title="Receive email with daily leads"
                description="A daily digest of every guest Priya captured the previous day."
                enabled={data.daily_leads_enabled}
                onToggle={() => set("daily_leads_enabled", !data.daily_leads_enabled)}
                emails={data.daily_leads_emails || []}
                onAdd={() => addEmail("daily_leads_emails", newLead, setNewLead)}
                onRemove={(i) => removeEmail("daily_leads_emails", i)}
                inputValue={newLead}
                setInputValue={setNewLead}
                testid="notif-daily-leads"
            />

            <NotifBlock
                title="Receive email with daily conversations"
                description="A daily digest of every chat Priya had the previous day."
                enabled={data.daily_conversations_enabled}
                onToggle={() => set("daily_conversations_enabled", !data.daily_conversations_enabled)}
                emails={data.daily_conversations_emails || []}
                onAdd={() => addEmail("daily_conversations_emails", newConv, setNewConv)}
                onRemove={(i) => removeEmail("daily_conversations_emails", i)}
                inputValue={newConv}
                setInputValue={setNewConv}
                testid="notif-daily-conversations"
            />

            <button
                onClick={save}
                disabled={saving}
                data-testid="notif-save"
                className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-60"
            >
                <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
        </div>
    );
}

const NotifBlock = ({ title, description, enabled, onToggle, emails, onAdd, onRemove, inputValue, setInputValue, testid }) => (
    <div className="bg-white border border-stone-200 rounded-lg p-6 mb-5" data-testid={testid}>
        <div className="flex items-start justify-between mb-4">
            <div>
                <h3 className="font-heading text-lg text-orchid-700 mb-0.5">{title}</h3>
                <p className="text-xs text-stone-500">{description}</p>
            </div>
            <button
                onClick={onToggle}
                data-testid={`${testid}-toggle`}
                className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                    enabled ? "bg-orchid-700" : "bg-stone-300"
                }`}
            >
                <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white ${
                        enabled ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </button>
        </div>

        {enabled && (
            <>
                <div className="flex items-center gap-2 mb-3">
                    <input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && onAdd()}
                        placeholder="name@orchidhotel.com"
                        data-testid={`${testid}-input`}
                        className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                    />
                    <button
                        onClick={onAdd}
                        data-testid={`${testid}-add`}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                    >
                        <Plus size={13} /> Add
                    </button>
                </div>
                {emails.length > 0 ? (
                    <ul className="space-y-1.5" data-testid={`${testid}-list`}>
                        {emails.map((e, i) => (
                            <li key={i} className="flex items-center justify-between px-3 py-2 rounded-md bg-stone-50 border border-stone-200">
                                <span className="text-sm text-stone-700">{e}</span>
                                <button
                                    onClick={() => onRemove(i)}
                                    data-testid={`${testid}-remove-${i}`}
                                    className="text-stone-400 hover:text-red-700 p-0.5"
                                >
                                    <X size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-xs text-stone-500 italic">No recipients yet.</p>
                )}
            </>
        )}
    </div>
);
